import { apiFetch } from "../utils/apiFetch";
import { apiUrl } from "../utils/api";

// ---------------------------------------------------------------- Types

export type ChatConversationType = "dm" | "group" | "channel";

/** Kompakte Vorschau der letzten Nachricht (2. Zeile im Listeneintrag). */
export interface ChatLastMessagePreview {
  id: string;
  body: string;
  authorUserId: string;
  authorName: string | null;
  /** Autor == aktueller Nutzer → Anzeige „Du: …". */
  isOwn: boolean;
  deleted: boolean;
}

export interface ChatConversation {
  id: string;
  tenantId: string;
  type: ChatConversationType;
  title?: string | null;
  topic?: string | null;
  visibility: "public" | "private";
  dmKey?: string | null;
  createdByUserId: string;
  lastMessageId?: string | null;
  lastMessageAt?: string | null;
  msgSeq: string;
  linkRefType?: string | null;
  linkRefId?: string | null;
  /** Geteilter Team-Chat: Verknüpfung mit einem AppTeam. */
  teamId?: string | null;
  createdAt: string;
  deleted: boolean;
  // Nur in listMine() angereichert:
  unreadCount?: number;
  unreadMentions?: number;
  lastReadMessageId?: string | null;
  lastMessage?: ChatLastMessagePreview | null;
}

/** Ein Team des Nutzers (> 1 Mitglied) für den „Teams"-Tab. `conversationId` ist
 *  null, solange der geteilte Team-Chat noch nicht angelegt wurde (lazy). */
export interface ChatTeamSummary {
  teamId: string;
  name: string;
  memberCount: number;
  conversationId: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
  lastMessage?: ChatLastMessagePreview | null;
}

export interface ChatMember {
  conversationId: string;
  userId: string;
  role: string;
  joinedAt: string;
  leftAt?: string | null;
  lastReadMessageId?: string | null;
  unreadCount: number;
  unreadMentions: number;
  notifyLevel: string;
}

export interface ChatAttachment {
  id: string;
  name: string;
  mimeType: string | null;
  size: number;
}

export interface ChatReplyPreview {
  id: string;
  authorName: string | null;
  body: string;
  deleted: boolean;
}

export type ChatMessageStatus = "sending" | "sent" | "failed";

export interface ChatMessage {
  id: string;
  conversationId: string;
  seq: string;
  tenantId: string;
  authorUserId: string;
  authorName?: string | null;
  body: string;
  clientMsgId: string;
  replyToId?: string | null;
  createdAt: string;
  editedAt?: string | null;
  deletedAt?: string | null;
  deletedByUserId?: string | null;
  attachments?: ChatAttachment[];
  replyTo?: ChatReplyPreview | null;
  pinned?: boolean;
  pinnedAt?: string | null;
  /** Nur clientseitig: optimistischer Sende-Status. */
  _status?: ChatMessageStatus;
}

export interface ReactionAggregate {
  messageId: string;
  conversationId: string;
  emoji: string;
  count: number;
  op: "add" | "remove";
  userId: string;
}

export type ReactionsByMessage = Record<
  string,
  Array<{ emoji: string; count: number; userIds: string[] }>
>;

export interface UnreadSummary {
  total: number;
  mentions: number;
}

// ---------------------------------------------------------------- HTTP helpers

const get = async <T>(path: string, params?: Record<string, string | number | boolean>): Promise<T> =>
  (await apiFetch<T>(path, { params })).data;

const send = async <T>(path: string, method: "POST" | "PATCH" | "DELETE", body?: unknown): Promise<T> =>
  (
    await apiFetch<T>(path, {
      method,
      headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  ).data;

// ---------------------------------------------------------------- Service

/** Antwort von `GET /chat/conversations/:id`. */
export interface ChatConversationDetail {
  conversation: ChatConversation;
  members: ChatMember[];
  /** Darf der Abrufende Mitglieder hinzufügen/entfernen? (Admin/GFGL/Teamleiter) */
  canManage: boolean;
  /** Darf der Abrufende die Gruppe löschen? (Admin/GFGL) */
  canDelete: boolean;
}

export const chatService = {
  listConversations: () => get<ChatConversation[]>("chat/conversations"),

  createConversation: (input: {
    type: ChatConversationType;
    title?: string;
    topic?: string;
    visibility?: "public" | "private";
    memberIds?: string[];
    linkRefType?: "KUNDE" | "VORGANG" | "SCHADEN" | "VERTRAG";
    linkRefId?: string;
  }) => send<ChatConversation>("chat/conversations", "POST", input),

  /** Conversation + aktive Mitglieder. `canManage`/`canDelete` kommen vom Server
   *  (Admin/GFGL/Teamleiter) — die Teamleiter-Rolle einer Chat-Gruppe ist im FE
   *  nicht ableitbar. */
  getConversation: (id: string) =>
    get<ChatConversationDetail>(`chat/conversations/${id}`),

  /** Mitglied zur Gruppe hinzufügen (Admin/GFGL/Teamleiter). */
  addGroupMember: (conversationId: string, userId: string) =>
    send<ChatMember>(`chat/conversations/${conversationId}/members`, "POST", { userId }),

  /** Mitglied aus der Gruppe entfernen (Admin/GFGL/Teamleiter). */
  removeGroupMember: (conversationId: string, userId: string) =>
    send<{ ok: boolean }>(
      `chat/conversations/${conversationId}/members/${encodeURIComponent(userId)}`,
      "DELETE",
    ),

  /** Gruppe löschen (Admin/GFGL) — Soft-Delete, das Team bleibt bestehen. */
  deleteGroup: (conversationId: string) =>
    send<{ ok: boolean }>(`chat/conversations/${conversationId}`, "DELETE"),

  getOrCreateDm: (userId: string) => get<ChatConversation>(`chat/dm/${userId}`),

  /** Teams des Nutzers (> 1 Mitglied) inkl. — falls vorhanden — Team-Chat. */
  listMyTeams: () => get<ChatTeamSummary[]>("chat/teams"),

  /** Idempotentes get-or-create des geteilten Team-Chats (+ Mitglieder-Sync). */
  getOrCreateTeamConversation: (teamId: string) =>
    get<ChatConversation>(`chat/team/${teamId}`),

  listMessages: (id: string, opts?: { beforeSeq?: string; limit?: number }) => {
    const params: Record<string, string | number> = {};
    if (opts?.beforeSeq) params.beforeSeq = opts.beforeSeq;
    if (opts?.limit) params.limit = opts.limit;
    return get<ChatMessage[]>(`chat/conversations/${id}/messages`, params);
  },

  listUpdatedMessages: (
    id: string,
    opts: { updatedSince: string; fromSeq?: string; toSeq?: string },
  ) => {
    const params: Record<string, string> = { updatedSince: opts.updatedSince };
    if (opts.fromSeq) params.fromSeq = opts.fromSeq;
    if (opts.toSeq) params.toSeq = opts.toSeq;
    return get<ChatMessage[]>(`chat/conversations/${id}/messages/updated`, params);
  },

  reactions: (id: string, messageIds: string[]) =>
    messageIds.length
      ? get<ReactionsByMessage>(`chat/conversations/${id}/reactions`, {
          messageIds: messageIds.join(","),
        })
      : Promise.resolve({} as ReactionsByMessage),

  sendMessage: (
    id: string,
    input: {
      body: string;
      clientMsgId: string;
      replyToId?: string;
      mentionedUserIds?: string[];
      attachmentIds?: string[];
    },
  ) => send<ChatMessage>(`chat/conversations/${id}/messages`, "POST", input),

  /** Datei hochladen (vor dem Senden) → Anhang-Metadaten. */
  uploadAttachment: async (conversationId: string, file: File): Promise<ChatAttachment> => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await apiFetch<ChatAttachment>(
      `chat/conversations/${conversationId}/attachments`,
      { method: "POST", body: fd },
    );
    return data;
  },

  /** Authentifizierte Download-/Anzeige-URL eines Anhangs (Cookie/Bearer-getragen). */
  attachmentDownloadUrl: (attachmentId: string): string =>
    apiUrl(`chat/attachments/${attachmentId}/download`),

  /** Anhang einem Vorgang zuweisen — legt ihn als Dokument am Vorgang (und beim
   *  Kunden des Vorgangs) ab. Idempotent: `status: "duplicate"`, wenn er dort
   *  schon liegt. */
  assignAttachmentToVorgang: (attachmentId: string, vorgangId: number) =>
    send<{ documentId: number | null; fileName: string; status: "created" | "duplicate" }>(
      `chat/attachments/${attachmentId}/assign`,
      "POST",
      { vorgangId },
    ),

  // ---- Phase 3: Suche & Pins ----
  searchMessages: (
    q: string,
    opts?: { conversationId?: string; from?: string; limit?: number },
  ) => {
    const params: Record<string, string | number> = { q };
    if (opts?.conversationId) params.conversationId = opts.conversationId;
    if (opts?.from) params.from = opts.from;
    if (opts?.limit) params.limit = opts.limit;
    return get<ChatMessage[]>("chat/search", params);
  },

  listPins: (conversationId: string) => get<ChatMessage[]>(`chat/conversations/${conversationId}/pins`),

  pinMessage: (messageId: string) =>
    send<{ id: string; conversationId: string; pinned: boolean }>(
      `chat/messages/${messageId}/pin`,
      "POST",
    ),

  unpinMessage: (messageId: string) =>
    send<{ id: string; conversationId: string; pinned: boolean }>(
      `chat/messages/${messageId}/pin`,
      "DELETE",
    ),

  editMessage: (messageId: string, body: string) =>
    send<ChatMessage>(`chat/messages/${messageId}`, "PATCH", { body }),

  deleteMessage: (messageId: string) =>
    send<{ id: string; conversationId: string; seq: string }>(`chat/messages/${messageId}`, "DELETE"),

  toggleReaction: (messageId: string, emoji: string) =>
    send<ReactionAggregate>(`chat/messages/${messageId}/reactions`, "POST", { emoji }),

  markRead: (id: string, lastReadMessageId: string) =>
    send<{ ok: true }>(`chat/conversations/${id}/read`, "POST", { lastReadMessageId }),

  getUnread: () => get<UnreadSummary>("chat/unread"),

  getSettings: () => get<{ readReceipts: boolean }>("chat/settings"),

  setSettings: (readReceipts: boolean) =>
    send<{ readReceipts: boolean }>("chat/settings", "POST", { readReceipts }),
};
